/** Kontoauszug (MP-B): Buchungen mit Saldo, Zeitraum, PDF, Verkäufe-CSV, Jahresübersicht, Auszahlung anfordern, Spenden-Option. */
import { useTx } from "../i18n";
import { useState } from "react";
import { Link } from "react-router";
import { Button, Dialog, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card, Dl } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro, parseEuro } from "@shared/geld";
import { formatTag, tagVon } from "@shared/zeit";
import { useMieterStart } from "./api";
import { useCockpit, usePortalSpeichern } from "./cockpit-api";
import { jahresPdfUrl, kontoPdfUrl, useAuszahlungAnfordern, useAuszahlungAnfragen, useKonto, verkaeufeCsvUrl } from "./konto-api";

const STATUS: Record<string, { s: "wait" | "paid" | "neutral"; t: string }> = { offen: { s: "wait", t: "Der Laden prüft" }, erledigt: { s: "paid", t: "Erledigt" }, abgelehnt: { s: "neutral", t: "Abgelehnt" } };

export function Konto() {
  const tx = useTx();
  const heute = tagVon();
  const jahr = Number(heute.slice(0, 4));
  const [von, setVon] = useState(`${jahr}-01-01`);
  const [bis, setBis] = useState(heute);
  const konto = useKonto(von, bis);
  const start = useMieterStart();
  const ck = useCockpit();
  const portal = usePortalSpeichern();
  const anfragen = useAuszahlungAnfragen();
  const anfordern = useAuszahlungAnfordern();
  const [dialog, setDialog] = useState(false);
  const [betrag, setBetrag] = useState("");
  const [meldung, setMeldung] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const guthaben = start.data?.guthabenCent ?? 0;
  const offen = anfragen.data?.anfragen.find((a) => a.status === "offen");
  const e = ck.data?.einstellungen;
  const betragCent = parseEuro(betrag);

  return (
    <div className="sc-col" style={{ gap: "var(--space-4)" }}>
      <div className="sc-row">
        <Link to="/mieter/abrechnungen" className="rg-btn rg-btn--quiet" aria-label="Zurück">‹</Link>
        <h1 className="title">{tx("Kontoauszug")}</h1>
      </div>
      {meldung ? <Notice tone={meldung.tone}>{meldung.text}</Notice> : null}
      <Card title={tx("Auszahlung")} actions={<span className="sc-strong">{formatEuro(guthaben)}</span>}>
        {offen ? (
          <Notice tone="info" title={`Angefordert: ${formatEuro(offen.betragCent)}`}>Der Laden erstellt eine Zwischenabrechnung und zahlt aus – du bekommst eine Nachricht.</Notice>
        ) : (
          <>
            <p className="sc-muted">{tx("Du musst nicht bis zum Periodenende warten: Fordere dein Guthaben jetzt an, der Laden bestätigt mit einem Tipp.")}</p>
            <Button variant="primary" icon="wallet" disabled={guthaben <= 0} onClick={() => { setBetrag((guthaben / 100).toFixed(2).replace(".", ",")); setDialog(true); }}>Auszahlung anfordern</Button>
          </>
        )}
        {anfragen.data?.anfragen.filter((a) => a.status !== "offen").slice(0, 3).map((a) => (
          <p key={a.id} className="sc-small sc-muted" style={{ margin: "var(--space-2) 0 0" }}>{formatTag(a.erstelltAt.slice(0, 10))}: {formatEuro(a.betragCent)} <StatusBadge status={STATUS[a.status]!.s}>{STATUS[a.status]!.t}</StatusBadge>{a.notiz ? ` · ${a.notiz}` : ""}</p>
        ))}
        {e ? (
          <label className="sc-check" style={{ marginTop: "var(--space-3)" }}>
            <input type="checkbox" checked={e.spende} onChange={(ev) => portal.mutate({ ...e, spende: ev.target.checked })} /> Kleine Restbeträge unter dem Mindestbetrag dem Laden spenden statt übertragen
          </label>
        ) : null}
      </Card>
      <Card title={tx("Zeitraum")}>
        <div className="sc-form">
          <TextField label="Von" type="date" value={von} onChange={(ev) => setVon(ev.target.value)} />
          <TextField label="Bis" type="date" value={bis} onChange={(ev) => setBis(ev.target.value)} />
        </div>
        <div className="sc-toolbar" style={{ marginTop: "var(--space-3)" }}>
          <a href={kontoPdfUrl(von, bis)} target="_blank" rel="noreferrer" className="rg-btn rg-btn--secondary">Kontoauszug (PDF)</a>
          <a href={verkaeufeCsvUrl(von, bis)} className="rg-btn rg-btn--secondary">Verkäufe (CSV)</a>
          <a href={jahresPdfUrl(jahr)} target="_blank" rel="noreferrer" className="rg-btn rg-btn--quiet">Jahresübersicht {jahr} (PDF)</a>
          <a href={jahresPdfUrl(jahr - 1)} target="_blank" rel="noreferrer" className="rg-btn rg-btn--quiet">{jahr - 1}</a>
        </div>
      </Card>
      <Seitenzustand query={konto} lines={6}>
        {(k) => (
          <Card title={tx("Buchungen")} flush>
            <div style={{ padding: "0 var(--space-4)" }}>
              <Dl rows={[["Anfangssaldo", formatEuro(k.anfangssaldoCent)], ["Endsaldo", <span key="e" className="sc-strong">{formatEuro(k.endsaldoCent)}</span>]]} />
            </div>
            <table className="rg-table">
              <thead><tr><th>Datum</th><th>Buchung</th><th className="is-num">Betrag</th><th className="is-num">Saldo</th></tr></thead>
              <tbody>
                {k.eintraege.length === 0 ? <tr><td colSpan={4} className="rg-table__empty">Keine Buchungen im Zeitraum.</td></tr> : null}
                {k.eintraege.map((z) => (
                  <tr key={z.id}><td>{formatTag(z.buchungsdatum)}</td><td>{z.text}{z.detail ? <div className="sc-muted sc-small">{z.detail}</div> : null}</td><td className="is-num" style={{ color: z.betragCent < 0 ? "var(--ink-muted)" : "var(--success)" }}>{formatEuro(z.betragCent)}</td><td className="is-num">{formatEuro(z.saldoCent)}</td></tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </Seitenzustand>
      <Dialog open={dialog} title="Auszahlung anfordern" size="sm" onClose={() => setDialog(false)} actions={<><Button variant="quiet" onClick={() => setDialog(false)}>Abbrechen</Button><Button variant="primary" icon="wallet" loading={anfordern.isPending} disabled={!betragCent || betragCent <= 0 || betragCent > guthaben} onClick={() => anfordern.mutate(betragCent!, { onSuccess: () => { setDialog(false); setMeldung({ tone: "success", text: "Angefordert – der Laden meldet sich." }); }, onError: (err) => { setDialog(false); setMeldung({ tone: "error", text: err instanceof ApiFehler ? err.message : "Das hat nicht geklappt." }); } })}>Anfordern</Button></>}>
        <div className="app-form">
          <TextField label="Betrag" value={betrag} onChange={(ev) => setBetrag(ev.target.value)} inputMode="decimal" suffix="€" hint={`Höchstens ${formatEuro(guthaben)} · ausgezahlt wird wie im Profil eingestellt`} autoFocus />
        </div>
      </Dialog>
    </div>
  );
}
